import { useEffect, useRef } from "react";
import katex from "katex";
import { cn } from "../../lib/utils";

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

function normalizeDelimiters(text: string): string {
  // \digit...\  →  \(...\)  e.g. \18{,}500\ → \(18{,}500\)
  text = text.replace(/\\(\d[^\\]*?)\\/g, (_, inner) => `\\(${inner}\\)`);
  // [ ... ] on its own line → \[ ... \]  (display math)
  text = text.replace(/^\[\s*([\s\S]*?)\s*\]$/gm, (_, inner) => `\\[${inner}\\]`);
  return text;
}

function renderMarkdown(raw: string): string {
  let html = normalizeDelimiters(raw);

  // Protect KaTeX display math \[...\]
  const displayMathBlocks: string[] = [];
  html = html.replace(/\\\[([\s\S]*?)\\\]/g, (_, latex) => {
    const idx = displayMathBlocks.length;
    try {
      displayMathBlocks.push(katex.renderToString(latex.trim(), { displayMode: true, throwOnError: false }));
    } catch {
      displayMathBlocks.push(`<span class="text-red-500">[Math Error]</span>`);
    }
    return `\x00dm${idx}\x00`;
  });

  // Protect KaTeX inline math \(...\)
  const inlineMathBlocks: string[] = [];
  html = html.replace(/\\\(([\s\S]*?)\\\)/g, (_, latex) => {
    const idx = inlineMathBlocks.length;
    try {
      inlineMathBlocks.push(katex.renderToString(latex.trim(), { displayMode: false, throwOnError: false }));
    } catch {
      inlineMathBlocks.push(`<span class="text-red-500">[Math Error]</span>`);
    }
    return `\x00im${idx}\x00`;
  });

  // Inline code (protect before other replacements)
  const codeBlocks: string[] = [];
  html = html.replace(/`([^`]+)`/g, (_, code) => {
    const idx = codeBlocks.length;
    codeBlocks.push(`<code class="bg-muted px-1 py-0.5 rounded text-sm font-mono">${code}</code>`);
    return `\x00cb${idx}\x00`;
  });

  // Headers
  html = html.replace(/^### (.+)$/gm, "<h3 class=\"text-base font-semibold mt-3 mb-1\">$1</h3>");
  html = html.replace(/^## (.+)$/gm, "<h2 class=\"text-lg font-semibold mt-4 mb-1\">$1</h2>");
  html = html.replace(/^# (.+)$/gm, "<h1 class=\"text-xl font-bold mt-4 mb-2\">$1</h1>");

  // Bold+italic: ***text***
  html = html.replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>");
  // Bold: **text**
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  // Italic: *text* or _text_
  html = html.replace(/\*([^*\n]+?)\*/g, "<em>$1</em>");
  html = html.replace(/_([^_\n]+?)_/g, "<em>$1</em>");

  // Tables: | col | col | with separator row |---|---|
  html = html.replace(/((?:^\|.+\|\n?)+)/gm, (block) => {
    const lines = block.trim().split("\n").filter((l) => l.trim());
    if (lines.length < 2) return block;
    const isSeparator = (l: string) => /^\|[\s|:-]+\|$/.test(l.trim());
    const sepIdx = lines.findIndex(isSeparator);
    if (sepIdx === -1) return block;
    const parseRow = (l: string) =>
      l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
    const headers = parseRow(lines[sepIdx - 1] ?? lines[0]);
    const bodyLines = lines.filter((_, i) => i !== sepIdx && i !== sepIdx - 1);
    const thead = `<thead><tr>${headers.map((h) => `<th class="border border-border px-3 py-1.5 text-left font-semibold bg-muted">${h}</th>`).join("")}</tr></thead>`;
    const tbody = `<tbody>${bodyLines.map((l) => `<tr>${parseRow(l).map((c) => `<td class="border border-border px-3 py-1.5">${c}</td>`).join("")}</tr>`).join("")}</tbody>`;
    return `<div class="overflow-x-auto my-3"><table class="border-collapse text-sm w-auto">${thead}${tbody}</table></div>`;
  });

  // Unordered lists
  html = html.replace(/((?:^[ \t]*[-*+] .+\n?)+)/gm, (block) => {
    const items = block.trim().split("\n").map((line) =>
      `<li>${line.replace(/^[ \t]*[-*+] /, "")}</li>`
    ).join("");
    return `<ul class="list-disc pl-5 my-2 space-y-1">${items}</ul>`;
  });

  // Ordered lists
  html = html.replace(/((?:^[ \t]*\d+\. .+\n?)+)/gm, (block) => {
    const items = block.trim().split("\n").map((line) =>
      `<li>${line.replace(/^[ \t]*\d+\. /, "")}</li>`
    ).join("");
    return `<ol class="list-decimal pl-5 my-2 space-y-1">${items}</ol>`;
  });

  // Paragraphs: double newline → separate paragraphs
  const paragraphs = html.split(/\n{2,}/);
  if (paragraphs.length > 1) {
    html = paragraphs.map((p) => {
      const trimmed = p.trim();
      if (!trimmed) return "";
      // Don't wrap block-level elements in <p>
      if (/^<(h[1-6]|ul|ol|li|blockquote|pre|div)/.test(trimmed)) return trimmed;
      return `<p class="mb-2">${trimmed}</p>`;
    }).join("");
  }

  // Single newlines → <br> (only if not already paragraph-split)
  if (paragraphs.length === 1) {
    html = html.replace(/\n/g, "<br />");
  }

  // Restore protected blocks
  html = html.replace(/\x00cb(\d+)\x00/g, (_, i) => codeBlocks[Number(i)]);
  html = html.replace(/\x00im(\d+)\x00/g, (_, i) => inlineMathBlocks[Number(i)]);
  html = html.replace(/\x00dm(\d+)\x00/g, (_, i) => displayMathBlocks[Number(i)]);

  return html;
}

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = renderMarkdown(content);
  }, [content]);

  return <div ref={containerRef} className={cn("latex-content", className)} />;
}
